// The US :location path segment is the slugified city, region and ZIP code
// joined by "--", e.g. "Beverly Hills" + "CA" + "90210" →
// "beverly-hills--ca--90210". The double dash exists to disambiguate the
// city/region split from the single dashes inside a multi-word city slug
// ("beverly-hills-ca" would be ambiguous). Encoding (search-form) and decoding
// (results loader) must stay in sync — both live here.
//
// The ZIP is part of the identity, not a suffix: several ZIPs share one city
// name (90210, 90211 and 90212 all map to "Beverly Hills, CA"), so the page a
// ZIP search canonicalizes to has to name the ZIP it searched (VIT-18219). The
// pre-VIT-18219 "<city>--<region>" form is gone from the grammar entirely — the
// results loader 301s the 21 curated cities' old URLs to their primary-ZIP
// pages and 404s the rest (see results.tsx).
//
// Outside the US the segment is the `intl-` form, which carries the ISO 3166-1
// country code instead of a state ("intl-auckland--nz"), because that's the
// qualifier worth showing: the foreign subdivision code is noise ("AUK" echoes
// the city, Tokyo's is "13", Singapore's is blank). See SearchForm for where
// that swap happens — the region set in us-regions.ts tells the two apart, so
// the results loader still derives everything it needs from the path alone.

export const slugify = (value: string): string =>
  value
    .trim()
    // Fold accented characters to their base letter ("San José" → "san jose")
    // so CloudFront's decoded city names produce clean ASCII slugs. NFKD
    // splits letters from their combining marks (U+0300–U+036F), which the
    // replace then strips.
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

export const formatLocationSlug = (city: string, region: string): string => `${slugify(city)}--${slugify(region)}`

// Inverse of formatLocationSlug, modulo slugify's lossiness: the city comes
// back lowercase with spaces restored ("beverly hills"). Returns null for
// anything that isn't exactly <city>--<region> with both parts non-empty, so
// hand-built/legacy URLs surface the error state instead of a garbage search.
export const parseLocationSlug = (slug: string): { city: string; region: string } | null => {
  const [citySlug, regionSlug, ...rest] = slug.split('--')
  if (citySlug == null || citySlug === '' || regionSlug == null || regionSlug === '' || rest.length > 0) {
    return null
  }
  const parsed = { city: citySlug.replace(/-/g, ' '), region: regionSlug }
  // Only accept canonical slugs — what formatLocationSlug itself would emit.
  // Splitting alone would admit variants like "beverly hills--CA" (raw space,
  // uppercase) or "houston---tx" (region "-tx"), giving the same search many
  // URLs and passing junk regions to the API.
  if (formatLocationSlug(parsed.city, parsed.region) !== slug) return null
  return parsed
}

// Display casing for a place name. Lowercases first, so it normalises the
// ALL-CAPS city the ZIP dataset carries (zip_centroids mirrors HUD, whose
// PREF_CITY is uppercase — VIT-17805) as well as the lowercase names
// parseLocationSlug emits. Apostrophes are word boundaries so "O'FALLON" reads
// "O'Fallon".
//
// "MCMINNVILLE" becomes "Mcminnville": no general rule recovers an inner
// capital, and a Mc/Mac exception list would mis-case Macon and Mchenry, so the
// simple rule wins.
export const toTitleCase = (value: string): string =>
  value
    .toLowerCase()
    .replace(/(^|[\s\-'])([a-z])/g, (_match, boundary: string, letter: string) => boundary + letter.toUpperCase())

// Display form of a parsed :location segment: "portland"/"or" → "Portland, OR".
// parseLocationSlug's output is lowercase (slugify lowercases on the way in),
// so the case has to be reconstructed here. Region codes are the 2-letter USPS
// abbreviations the geo lookup and the browse-by-city table both emit, so they
// uppercase whole; anything longer is title-cased instead so a hand-built URL
// can't render "DISTRICT OF COLUMBIA".
export const formatLocationLabel = ({ city, region }: { city: string; region: string }): string =>
  `${toTitleCase(city)}, ${region.length <= 3 ? region.toUpperCase() : toTitleCase(region)}`

// A :location path segment is one of three forms: "<city>--<region>--<zip>",
// "zip-<5 digits>" or "intl-<city>--<country>" (VIT-18219, VIT-17740, VIT-17624,
// docs/url-structure.md). Bare 5-digit segments deliberately do NOT parse — the
// `zip-` prefix keeps ZIP segments unambiguous against city slugs, so each search
// still has exactly one canonical URL.
const ZIP_SLUG_RE = /^zip-(\d{5})$/

// A location outside our coverage area, carrying the ISO 3166-1 country code
// instead of a US state: "intl-toronto--ca".
//
// The `intl-` prefix is what makes the country unambiguous, and it is load-bearing
// rather than decorative. 27 ISO 3166-1 alpha-2 codes collide with USPS state
// abbreviations — CA is Canada and California, DE Germany and Delaware, IN India
// and Indiana, PR both Puerto Rico and (as an ISO 3166-2 subdivision) Paraná — so
// a country sharing the region slot would be indistinguishable from a state, and
// the results loader would search Toronto, California instead of reporting that
// Canada is outside coverage.
const INTL_SLUG_RE = /^intl-(.+)--([a-z]{2})$/

export type ResolvedLocation =
  | { kind: 'city'; city: string; region: string; postalCode: string }
  | { kind: 'zip'; postalCode: string }
  | { kind: 'intl'; city: string; country: string }

export const formatZipSlug = (postalCode: string): string => `zip-${postalCode}`

// The US city form: "<city>--<region>--<zip>". The ZIP travels in the segment
// rather than a query param so the canonical URL of a ZIP search names the ZIP
// it searched (VIT-18219).
export const formatCityZipSlug = (city: string, region: string, postalCode: string): string =>
  `${formatLocationSlug(city, region)}--${postalCode}`

export const formatIntlSlug = (city: string, country: string): string => `intl-${slugify(city)}--${slugify(country)}`

// Parses any location form. Returns null for anything that is none of them, so
// the results loader surfaces its 404 rather than running a garbage search.
export const parseLocation = (slug: string): ResolvedLocation | null => {
  const zip = ZIP_SLUG_RE.exec(slug)?.[1]
  if (zip != null) return { kind: 'zip', postalCode: zip }

  // The intl- and city forms can't collide — one ends in an alpha-2 country,
  // the other in 5 digits — but the intl- arm must still be tried on its own:
  // a segment the city parse below would reject ("intl-toronto--ca" has no ZIP)
  // is a valid outside-coverage location, not a 404 (VIT-17624).
  const intl = INTL_SLUG_RE.exec(slug)
  if (intl != null) {
    const parsed = { kind: 'intl' as const, city: (intl[1] ?? '').replace(/-/g, ' '), country: intl[2] ?? '' }
    // Only accept canonical slugs, the same rule parseLocationSlug applies.
    return formatIntlSlug(parsed.city, parsed.country) === slug ? parsed : null
  }

  const [citySlug, regionSlug, postalCode, ...rest] = slug.split('--')
  if (
    citySlug == null ||
    citySlug === '' ||
    regionSlug == null ||
    regionSlug === '' ||
    postalCode == null ||
    !/^\d{5}$/.test(postalCode) ||
    rest.length > 0
  ) {
    // Includes the pre-VIT-18219 <city>--<region> form, which now 404s.
    return null
  }
  const parsed = {
    kind: 'city' as const,
    city: citySlug.replace(/-/g, ' '),
    region: regionSlug,
    postalCode,
  }
  // Only accept canonical slugs — what formatCityZipSlug itself would emit —
  // the same rule parseLocationSlug applies.
  return formatCityZipSlug(parsed.city, parsed.region, parsed.postalCode) === slug ? parsed : null
}

// The display label for a parsed segment lives in `api/results-search.server.ts`'s
// `resultsSearchLabels` (VIT-18197). It was here as `formatResolvedLocationLabel`, which
// returned only the joined "City, ST ZIP" form — the card needs the place and the ZIP
// apart, because it sets the ZIP small beside its URL line rather than at headline size.
// Composing the joined form from those two parts keeps one implementation; a second one
// here would be free to drift from it.

// A provider path segment is `npi-<10 digits>` followed by an optional decorative
// slug, e.g. "npi-1861770604-amnon-beniaminovitz" or bare "npi-1861770604". The NPI
// is the only lookup key; the tail is never read back (VIT-17797), so any spelling
// of it — or none at all — serves.
//
// NPI-FIRST, and the `npi-` prefix, are both load-bearing:
//   - CloudFront invalidation paths accept only a TRAILING wildcard, so the NPI has
//     to lead for `/us/provider/npi-1861770604*` to be a legal path clearing every
//     slug variant of one provider. NPI-trailing has no expressible equivalent.
//   - The prefix makes the segment self-describing and unambiguous against the old
//     `<slug>--<npi>` order without counting digits — the same discriminator idiom
//     as `zip-` and `intl-` above, and the same shape as the canonical headshot key
//     (apps/providers/src/headshots/object-key.ts).
const PROVIDER_SLUG_RE = /^npi-(\d{10})(?:-.+)?$/

export const formatProviderSlug = (npi: string, name: string): string => {
  const slug = slugify(name)
  return `npi-${npi}${slug === '' ? '' : `-${slug}`}`
}

// Returns the NPI only — the tail is deliberately discarded rather than validated,
// so a stale or wrong slug still renders the provider instead of 404ing or
// redirecting. The page emits <link rel=canonical> so the variants don't compete in
// search, and a single prefix invalidation clears all of them.
//
// Null for anything that isn't that shape — including the old `<slug>--<npi>` order,
// which 404s rather than redirecting: the site is pre-launch and carries no
// legacy-URL compatibility (docs/url-structure.md).
export const parseProviderNpi = (segment: string): string | null => PROVIDER_SLUG_RE.exec(segment)?.[1] ?? null
